import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import {
  ScaleButton,
  ScaleIconActionClose,
  ScaleIconActionSend,
  ScaleIconContentSupportChat,
  ScaleModal,
} from '@cloudorder/ui';

import { explain, greeting, suggestionsFor } from '@/features/pixie/answers';
import { usePixie } from '@/features/pixie/PixieContext';
import { caseIdFromPath, caseRoute } from '@/features/pixie/route';
import { runSkills } from '@/features/pixie/skills';
import type { PixieAction, PixieAnswer, PixieEntry, PixieData } from '@/features/pixie/types';
import {
  useCases,
  useCharacteristics,
  useCustomers,
  useDossier,
  useEvidence,
  useMails,
  useOfferings,
  useOpenGaps,
} from '@/shared/api/queries';
import { findCase } from '@/shared/domain/selectors';
import type { CaseRecord } from '@/shared/domain/case';
import { useVocabulary } from '@/shared/domain/useVocabulary';
import { DASH } from '@/shared/i18n/format';
import { useFormatter } from '@/shared/i18n/useFormatter';
import { Pill } from '@/shared/ui/Pill';
import { toneFor } from '@/shared/ui/tone';

import styles from './Pixie.module.css';

/* The panel. It is mounted only while Pixie is open, so a cockpit that never
   opens it never fetches the catalog, the customers or the gap map.

   This is where Pixie's language comes from. An answer is a plain function, not
   a component, so the translator and the number formatter are resolved once here
   and handed into `PixieData` alongside the vocabulary — the same seam, for the
   same reason. Both are in the memo's dependency list, so switching language
   rebuilds the bundle and the next answer is written in the new one. */

const THINKING_MS = 420;

function reducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function PixiePanel(): ReactElement | null {
  const { open, log, pending, ask, say, resolve, close } = usePixie();
  const navigate = useNavigate();
  const location = useLocation();
  const caseId = caseIdFromPath(location.pathname);

  const cases = useCases();
  const mails = useMails();
  const characteristics = useCharacteristics();
  const customers = useCustomers();
  const offerings = useOfferings();
  const gaps = useOpenGaps();
  const dossier = useDossier(caseId);
  const evidence = useEvidence();
  const vocabulary = useVocabulary();
  const { t } = useTranslation(['pixie', 'domain', 'case', 'common']);
  const format = useFormatter();

  const [question, setQuestion] = useState('');
  const [cite, setCite] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  /* One bundle, rebuilt only when something in it actually changed. It is the
     scope boundary: no case in the URL means no case data reaches an answer. */
  const data = useMemo<PixieData | null>(() => {
    if (!cases.data || !mails.data || !characteristics.data) return null;
    if (!customers.data || !offerings.data || !gaps.data) return null;
    if (caseId && !dossier.data) return null;
    const record = caseId ? (findCase(cases.data, caseId) ?? null) : null;
    return {
      cases: cases.data,
      mails: mails.data,
      characteristics: characteristics.data,
      mriFields: dossier.data?.mriFields ?? [],
      customers: customers.data,
      offerings: offerings.data,
      gaps: gaps.data,
      caseRecord: record,
      detail: dossier.data?.detail ?? null,
      vocabulary,
      t,
      format,
    };
  }, [
    cases.data,
    mails.data,
    characteristics.data,
    customers.data,
    offerings.data,
    gaps.data,
    dossier.data,
    caseId,
    vocabulary,
    t,
    format,
  ]);

  // Greet once, when there is enough loaded to say what the scope is.
  useEffect(() => {
    if (!open || !data || log.length > 0) return;
    say(greeting(data));
  }, [open, data, log.length, say]);

  // Answer whatever is pending, after a pause that reads as thought rather than
  // latency — and no pause at all where motion is unwelcome.
  useEffect(() => {
    if (pending === null || !data) return;
    const timer = window.setTimeout(
      () => {
        const answer = runSkills(pending, data) ?? explain(pending, data);
        resolve(answer);
        if (answer.navigate) navigate(answer.navigate);
      },
      reducedMotion() ? 0 : THINKING_MS,
    );
    return () => window.clearTimeout(timer);
  }, [pending, data, resolve, navigate]);

  useEffect(() => {
    const element = logRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [log, pending]);

  if (!open) return null;

  function send(): void {
    ask(question);
    setQuestion('');
  }

  function runAction(action: PixieAction): void {
    if (action.kind === 'ask') ask(action.question);
    else void navigate(action.to);
  }

  function openCase(record: CaseRecord): void {
    void navigate(caseRoute(record));
  }

  const scope = data?.caseRecord
    ? t('pixie:panel.scoped', {
        id: data.caseRecord.id,
        customer: vocabulary.caseCustomer(data.caseRecord),
      })
    : t('pixie:panel.unscoped');
  const cited = cite === null ? null : evidence.data?.[cite];

  return (
    <aside className={styles.panel} id="pixie-panel" aria-label={t('pixie:panel.label')}>
      <header className={styles.head}>
        <div className={styles.avatar}>
          <ScaleIconContentSupportChat size={18} decorative />
        </div>
        <div>
          {/* The assistant's name, not a label — it is the same word in both. */}
          <h2>Pixie</h2>
          <span className={styles.scope}>{scope}</span>
        </div>
        <span className={styles.close}>
          <ScaleButton
            size="small"
            variant="secondary"
            iconOnly
            innerAriaLabel={t('pixie:panel.close')}
            onClick={close}
          >
            <ScaleIconActionClose decorative />
          </ScaleButton>
        </span>
      </header>

      <div className={styles.log} ref={logRef} aria-live="polite">
        {log.map((entry) => (
          <Bubble
            key={entry.id}
            entry={entry}
            onCite={setCite}
            onAction={runAction}
            onOpenCase={openCase}
          />
        ))}
        {pending !== null ? <Thinking /> : null}
      </div>

      {data ? (
        <div className={styles.suggest}>
          {suggestionsFor(data).map((chip) => (
            <button key={chip.label} type="button" onClick={() => ask(chip.question)}>
              {chip.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.composer}>
        <textarea
          rows={1}
          value={question}
          aria-label={t('pixie:panel.composerLabel')}
          placeholder={t('pixie:panel.placeholder')}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
        />
        <span className={styles.send}>
          <ScaleButton size="small" variant="primary" onClick={send} disabled={data === null}>
            <ScaleIconActionSend slot="icon-before" decorative />
            {t('pixie:panel.ask')}
          </ScaleButton>
        </span>
      </div>

      <p className={styles.foot}>{t('pixie:panel.foot')}</p>

      <ScaleModal
        heading={
          cite === null ? t('pixie:evidence.title') : t('pixie:evidence.titleCited', { cite })
        }
        opened={cite !== null}
        size="small"
        onScaleClose={() => setCite(null)}
      >
        {/* The locator is tested beside the record so both are narrowed inside:
            the heading is written from the two together, and an evidence entry
            is only ever reachable through the locator it is filed under. */}
        {cite !== null && cited ? (
          <>
            <p>
              <Trans
                ns="pixie"
                i18nKey="evidence.citedBy"
                values={{ title: vocabulary.evidenceTitle(cited, cite) }}
                components={{ b: <b /> }}
              />
            </p>
            <div className={styles.quote}>{cited.quote}</div>
          </>
        ) : (
          <p>{t('pixie:evidence.retained')}</p>
        )}
      </ScaleModal>
    </aside>
  );
}

function Thinking(): ReactElement {
  const { t } = useTranslation('pixie');
  return (
    <div className={styles.msg}>
      <div className={styles.who} aria-hidden="true">
        PX
      </div>
      <div className={styles.bubble}>
        <span className={styles.typing} role="status" aria-label={t('panel.answering')}>
          <i />
          <i />
          <i />
        </span>
      </div>
    </div>
  );
}

function Bubble({
  entry,
  onCite,
  onAction,
  onOpenCase,
}: {
  readonly entry: PixieEntry;
  readonly onCite: (cite: string) => void;
  readonly onAction: (action: PixieAction) => void;
  readonly onOpenCase: (record: CaseRecord) => void;
}): ReactElement {
  const { answer }: { answer: PixieAnswer } = entry;
  const mine = entry.from === 'you';
  /* Its own subscription rather than a prop off the panel: a bubble is rendered
     once per entry and re-renders on a language change either way, and threading
     the vocabulary through would put it in the signature of every future bubble
     that has nothing to say about a case. The translator arrives the same way and
     for the same reason. */
  const vocabulary = useVocabulary();
  const { t } = useTranslation('pixie');
  return (
    <div className={`${styles.msg} ${mine ? styles.out : ''}`}>
      <div className={styles.who} aria-hidden="true">
        {mine ? t('panel.you') : 'PX'}
      </div>
      <div className={`${styles.bubble} ${answer.refusal ? styles.refusal : ''}`}>
        <div className={styles.meta}>
          {/* “Pixie” is the assistant's name and stays as it is in every locale. */}
          <b>{mine ? t('panel.you') : 'Pixie'}</b>
          {entry.at}
        </div>

        {answer.text}

        {answer.list && answer.list.length > 0 ? (
          <div className={styles.hits}>
            {answer.list.map((record) => (
              <button
                key={record.id}
                type="button"
                className={styles.hit}
                onClick={() => onOpenCase(record)}
              >
                <span className={styles.hitId}>{record.id}</span>
                <span className={styles.hitPill}>
                  <Pill tone={toneFor(record.disposition)}>{vocabulary.caseState(record)}</Pill>
                </span>
                <span className={styles.hitSub}>
                  {vocabulary.caseCustomer(record)} · {record.offering || DASH}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {answer.cites && answer.cites.length > 0 ? (
          <div className={styles.cites}>
            {answer.cites.map((source) => (
              <button
                key={source}
                type="button"
                className={styles.cite}
                onClick={() => onCite(source)}
              >
                {source}
              </button>
            ))}
          </div>
        ) : null}

        {answer.actions && answer.actions.length > 0 ? (
          <div className={styles.actions}>
            {answer.actions.map((action) => (
              <ScaleButton
                key={action.label}
                variant="secondary"
                size="small"
                onClick={() => onAction(action)}
              >
                {action.label}
              </ScaleButton>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
